import { h, type Ref } from 'vue'
import { getAlgoliaResults } from '@algolia/autocomplete-js'
import { Map, MapPin, Package } from 'lucide-vue-next'
import {
  getLocationsIndexName,
  getCategoriesIndexName,
  getFederatedSearchIndexName,
  getBoxesIndexName,
  getFederatedSearchAnalyticsTags,
  type FederatedSearchAnalyticsPlacement,
} from '~/utils/algoliaHelpers'
import { sanitizeAlgoliaQueryId } from '~/utils/helpers'
import { eventGetters } from '~/utils/eventGetters'
import {
  getProductPath,
  mountUrlParamsFromItem,
  getLocationDisplayName,
  getLocationNameAttributeForAlgolia,
  mergeEventsWithKits,
  GEO_PRECISION,
  HITS_PER_PAGE,
} from './helpers'
import { useFederatedSearchAnalyticsSession } from '~/composables/algolia/useFederatedSearchAnalyticsSession'
import { applyAlgoliaSearchAnalyticsGuard } from '~/utils/algoliaSearchAnalytics'

interface SourceDependencies {
  searchClient: any
  locale: Ref<string>
  isMobile: boolean
  localePath: (path: string) => string
  t: (key: string) => string
  router: any
  eventsQueryId: Ref<string | null>
  // Total matching events for the query (Algolia `nbHits`), independent of how
  // many rows the panel actually renders — used for the GA4 `search` event.
  eventsNbHits: Ref<number | null>
  appEnv: string
  analyticsPlacement?: FederatedSearchAnalyticsPlacement
}

const withFederatedSearchAnalytics = (
  appEnvironment: string,
  placement: FederatedSearchAnalyticsPlacement | undefined,
  params: Record<string, unknown>,
  query: string
) =>
  applyAlgoliaSearchAnalyticsGuard(
    {
      ...params,
      clickAnalytics: true,
      analyticsTags: [
        ...getFederatedSearchAnalyticsTags(appEnvironment, placement),
      ],
    },
    query
  )

export const useFederatedSearchSources = (deps: SourceDependencies) => {
  const {
    searchClient,
    locale,
    isMobile,
    localePath,
    t,
    router,
    eventsQueryId,
    eventsNbHits,
    appEnv,
    analyticsPlacement,
  } = deps

  const { setFederatedSearchAnalyticsOutcome } =
    useFederatedSearchAnalyticsSession()

  const createLocalitiesSource = () => ({
    sourceId: 'locations',
    getItemInputValue: ({ item }: any) =>
      getLocationDisplayName(item, locale.value),
    getItems({ query }: any) {
      const nameAttr = getLocationNameAttributeForAlgolia(locale.value)
      const indexName = getLocationsIndexName()
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName,
            query,
            params: withFederatedSearchAnalytics(
              appEnv,
              analyticsPlacement,
              {
                hitsPerPage: isMobile
                  ? HITS_PER_PAGE.locations.mobile
                  : HITS_PER_PAGE.locations.desktop,
                attributesToSnippet: [`${nameAttr}:24`],
                snippetEllipsisText: '…',
                optionalWords: query,
                optionalFilters: ['type:locality'],
                aroundLatLngViaIP: true,
                aroundRadius: 'all',
                aroundPrecision: GEO_PRECISION,
              },
              query
            ),
          },
        ],
        transformResponse({ hits }: any) {
          return hits.map((indexHits: any[]) => indexHits)
        },
      })
    },
    transformResponse({ hits }: any) {
      return hits.map((indexHits: any[]) =>
        indexHits.filter((h) => h.type !== 'admin_area')
      )
    },
    getItemUrl({ item }: any) {
      return localePath(`/search/?${mountUrlParamsFromItem(item)}`)
    },
    templates: {
      header() {
        return h('div', { class: 'mr-8' }, [
          h(
            'span',
            { class: 'aa-SourceHeaderTitle' },
            t('home.header.search.selectCity')
          ),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item, components }: any) {
        const nameAttr = getLocationNameAttributeForAlgolia(locale.value)
        const LocationIcon = item?.type === 'region' ? Map : MapPin
        return h(
          'div',
          {
            class: 'aa-ItemContent items-center gap-2',
            'data-test-id': 'federated-search-location-item',
          },
          [
            h(LocationIcon, {
              class: 'shrink-0 text-stone-600',
              size: 20,
              'aria-hidden': true,
            }),
            h('span', { class: 'aa-ItemContentTitle' }, [
              item?._highlightResult?.[nameAttr]
                ? components.Highlight({ hit: item, attribute: nameAttr })
                : getLocationDisplayName(item, locale.value),
            ]),
          ]
        )
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      setFederatedSearchAnalyticsOutcome('location_results')
      router.push(localePath(`/search/?${mountUrlParamsFromItem(item)}`))
    },
  })

  const createCategoriesSource = () => ({
    sourceId: 'eventCategories',
    getItemInputValue: ({ item }: any) => item.query,
    getItems({ query }: any) {
      const indexName = getCategoriesIndexName(locale.value)
      return getAlgoliaResults({
        searchClient,
        queries: [
          {
            indexName,
            query,
            params: withFederatedSearchAnalytics(
              appEnv,
              analyticsPlacement,
              {
                hitsPerPage: HITS_PER_PAGE.categories,
                attributesToSnippet: ['name:24'],
                snippetEllipsisText: '…',
                optionalWords: query,
              },
              query
            ),
          },
        ],
      })
    },
    getItemUrl({ item }: any) {
      return localePath(`/c/${item?.slug}`)
    },
    templates: {
      header() {
        return h('div', null, [
          h('span', { class: 'aa-SourceHeaderTitle' }, t('general.categories')),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item, components }: any) {
        return h(
          'div',
          {
            class: 'aa-ItemContent',
            'data-test-id': 'federated-search-category-item',
          },
          [
            h('span', { class: 'aa-ItemContentTitle' }, item.name),
            components.Highlight({ hit: item, attribute: 'name' }),
          ]
        )
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      setFederatedSearchAnalyticsOutcome('category_results')
      router.push(localePath(`/c/${item.slug}`))
    },
  })

  const createEventsSource = (setContext: any) => ({
    sourceId: 'events',
    getItemInputValue: ({ item }: any) => item.query,
    getItems({ query }: any) {
      const indexName = getFederatedSearchIndexName(locale.value)
      const kitsIndexName = getBoxesIndexName(locale.value)
      const eventParams = withFederatedSearchAnalytics(
        appEnv,
        analyticsPlacement,
        {
          hitsPerPage: HITS_PER_PAGE.events,
          attributesToSnippet: ['name:24'],
          snippetEllipsisText: '…',
          aroundLatLngViaIP: true,
          aroundRadius: 'all',
          aroundPrecision: GEO_PRECISION,
        },
        query
      )
      return getAlgoliaResults({
        searchClient,
        queries: [
          { indexName, query, params: eventParams },
          { indexName: kitsIndexName, query, params: eventParams },
        ],
        transformResponse({ results, hits }: any) {
          const [eventHits, kitHits] = hits
          eventsQueryId.value = results?.[0]?.queryID ?? null
          // Full match count for the events index (mirrors what the /search
          // page reports as `nbHits`), not the capped list shown in the panel.
          eventsNbHits.value = results?.[0]?.nbHits ?? null
          setContext({
            redirects: results?.[0]?.renderingContent?.redirect,
          })
          return mergeEventsWithKits(
            eventHits,
            kitHits ?? [],
            HITS_PER_PAGE.events
          )
        },
      })
    },
    getItemUrl({ item }: any) {
      return item.url + ' '
    },
    templates: {
      header() {
        return h('div', null, [
          h('span', { class: 'aa-SourceHeaderTitle' }, t('general.events')),
          h('div', { class: 'aa-SourceHeaderLine' }),
        ])
      },
      item({ item }: any) {
        const isKit = eventGetters.isBoxEvent(item)
        return h(
          'div',
          {
            class: 'aa-ItemContent',
            'data-test-id': isKit
              ? 'federated-search-kit-item'
              : 'federated-search-event-item',
          },
          [
            h('img', {
              class: 'w-20 h-16 object-cover mr-2 rounded-md',
              src: item.main_image?.thumb,
              alt: item.main_image?.alt,
            }),
            h('div', { class: 'flex flex-col' }, [
              h('span', { class: 'aa-ItemContentTitle' }, item.title),
              isKit &&
                h(
                  'span',
                  {
                    class:
                      'inline-flex items-center gap-1 w-fit text-xs font-medium text-teal-900 bg-teal-50 px-2 mt-[3px] py-0.5 rounded-full',
                  },
                  [
                    h(Package, {
                      size: 12,
                      class: 'shrink-0',
                      'aria-hidden': true,
                    }),
                    t('search.algoliaFederatedSearch.kitBadge'),
                  ]
                ),
            ]),
          ]
        )
      },
      noResults() {
        return t('general.select.noResultsFound')
      },
    },
    onSelect({ item, setIsOpen }: any) {
      setIsOpen(false)
      const path = localePath(getProductPath(item))
      const qid = sanitizeAlgoliaQueryId(eventsQueryId.value)
      const url = qid ? `${path}?qid=${qid}` : path
      window.location.href = url
    },
  })

  return ({ setContext }: any) => [
    createLocalitiesSource(),
    createCategoriesSource(),
    createEventsSource(setContext),
  ]
}
